import './style.css'
import { Particle } from '@core/components/home/Hero/Particle.ts'

interface HeroProps {
  heroContainer: HTMLElement | null
  canvas: HTMLCanvasElement | null
}

export class Hero {
  private readonly particleMaxSize: number = 3
  private readonly particleMinSize: number = 1
  private readonly particleSpeed: number = 0.3
  private readonly particleMaxCount: number = 200
  private readonly particleMinCount: number = 40
  private readonly primaryColor = 'rgba(0,191,255,0.8)'
  private readonly secondaryColor = 'rgba(255,0,255,0.8)'

  private readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  private heroContainer: HTMLElement

  private particlesArray: Particle[] = []

  constructor({ canvas, heroContainer }: HeroProps) {
    if (!canvas || !heroContainer) {
      throw new Error('Le canvas et son conteneur sont requis')
    }

    this.canvas = canvas
    const context = this.canvas.getContext('2d')
    if (!context) {
      throw new Error('Impossible de récupérer le contexte du canvas')
    }
    this.ctx = context

    this.heroContainer = heroContainer

    window.addEventListener('resize', this.init.bind(this))
  }

  public animate(): void {
    requestAnimationFrame(this.animate.bind(this))
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)

    for (let i = 0; i < this.particlesArray.length; i++) {
      this.particlesArray[i].update(this.canvas, this.ctx)
    }
    this.drawConnect()
  }

  public drawConnect(): void {
    let opacityValue = 1
    for (let a = 0; a < this.particlesArray.length; a++) {
      for (let b = a; b < this.particlesArray.length; b++) {
        const distance =
          (this.particlesArray[a].x - this.particlesArray[b].x) *
            (this.particlesArray[a].x - this.particlesArray[b].x) +
          (this.particlesArray[a].y - this.particlesArray[b].y) * (this.particlesArray[a].y - this.particlesArray[b].y)
        if (distance < (this.canvas.width / 7) * (this.canvas.height / 7)) {
          // Distance de connexion
          opacityValue = 1 - distance / 20000 // Opacité basée sur la distance
          const lineColorA = this.particlesArray[a].getColorAfterReplace(opacityValue)
          const lineColorB = this.particlesArray[b].getColorAfterReplace(opacityValue)

          // Créer un dégradé pour la ligne
          const gradient = this.ctx.createLinearGradient(
            this.particlesArray[a].x,
            this.particlesArray[a].y,
            this.particlesArray[b].x,
            this.particlesArray[b].y,
          )
          gradient.addColorStop(0, lineColorA)
          gradient.addColorStop(1, lineColorB)

          this.ctx.strokeStyle = gradient
          this.ctx.lineWidth = 1
          this.ctx.beginPath()
          this.ctx.moveTo(this.particlesArray[a].x, this.particlesArray[a].y)
          this.ctx.lineTo(this.particlesArray[b].x, this.particlesArray[b].y)
          this.ctx.stroke()
        }
      }
    }
  }

  public init(): void {
    this.resizeCanvas()
    this.particlesArray = []
    let numberOfParticles = Math.round((this.canvas.height * this.canvas.width) / 9000)
    if (numberOfParticles > this.particleMaxCount) numberOfParticles = this.particleMaxCount // Limiter le nombre max
    if (numberOfParticles < this.particleMinCount) numberOfParticles = this.particleMinCount // Limiter le nombre min

    for (let i = 0; i < numberOfParticles; i++) {
      const size = Math.random() * this.particleMaxSize + this.particleMinSize // Taille entre 1 et 4
      this.particlesArray.push(
        new Particle({
          x: Math.random() * (this.canvas.width - size * 4) + size * 2,
          y: Math.random() * (this.canvas.height - size * 4) + size * 2,
          directionX: Math.random() * this.particleSpeed - this.particleSpeed / 2,
          directionY: Math.random() * this.particleSpeed - this.particleSpeed / 2,
          size,
          color: i % 2 === 0 ? this.primaryColor : this.secondaryColor,
        }),
      )
    }
  }

  private resizeCanvas(): void {
    this.canvas.width = this.heroContainer.offsetWidth
    this.canvas.height = this.heroContainer.offsetHeight
  }
}
